import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import {
  AccountType,
  HttpSharedContext,
  MenuItemType,
  Rights,
} from '@shared/enums';
import {
  NavigationMenuItem,
  UserPreferences,
  UserSession,
} from '@shared/models';
import { AppFacade, UserDetailsFacade } from '@shared/state/facades';
import {
  CryptoService,
  GenericHttpClientService,
  SelectedAccountService,
  SignOutService,
  ZendeskService,
} from '@shared/services';
import { Constants } from '@shared/utils';
import { SelectClientDialogComponent } from '@shared/components';
import { UserPreferencesDialogComponent } from '@shared/components';

@UntilDestroy()
@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidebarComponent implements OnInit {
  @Input({ required: true }) isHandset: boolean;

  isAppOnline$ = this.appFacade.query.isAppOnline$;
  sidebarPinned$ = this.appFacade.query.sidebarStatus$;
  baseAccount$ = this.userDetailsFacade.query.baseAccount$;
  selectedClient$ = this.userDetailsFacade.query.selectedClient$;

  // Support (Zendesk) custom launcher
  supportUnreadCount$ = this.zendeskService.unreadCount$;
  supportHasUnread$ = this.zendeskService.unreadCount$.pipe(
    map((count) => count > 0)
  );
  isSupportOpen$ = this.zendeskService.isOpen$;
  canShowSupport$ = this.zendeskService.canShowWidget$;

  isAppOnline: boolean;
  clientId: string;
  subprojectId: string;

  menuItems$ = new BehaviorSubject<NavigationMenuItem[]>([]);
  selectClientDialog: MatDialogRef<SelectClientDialogComponent>;
  visibleMenuItems: MenuItemType[] = [];
  userDetails: UserSession;
  isDprDetails: boolean;

  protected dialogRef?: MatDialogRef<any, any>;

  constructor(
    private appFacade: AppFacade,
    private userDetailsFacade: UserDetailsFacade,
    private changeDetectorRef: ChangeDetectorRef,
    private cryptoService: CryptoService,
    private selectedAccountService: SelectedAccountService,
    private dialog: MatDialog,
    private router: Router,
    private genericHttpClientService: GenericHttpClientService,
    private signOutService: SignOutService,
    private zendeskService: ZendeskService
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        untilDestroyed(this),
        filter((r) => r instanceof NavigationEnd),
        tap(() => {
          this.checkActivatedRoute();
        })
      )
      .subscribe();

    this.userDetailsFacade.query.userDetails$
      .pipe(
        untilDestroyed(this),
        tap((userSession) => {
          this.userDetails = userSession;
        })
      )
      .subscribe();

    combineLatest([
      this.isAppOnline$,
      this.userDetailsFacade.query.userDetails$,
    ])
      .pipe(
        untilDestroyed(this),
        filter(([_, userDetails]) => !!userDetails),
        tap(([isAppOnline, userDetails]) => {
          this.clientId = userDetails.selectedClientId;
          this.subprojectId = this.cryptoService.encryptId(
            userDetails.selectedSubproject?.id
          );
          this.buildVisibleMenuItems(userDetails);
          this.setMenuItems(isAppOnline);

          this.checkActivatedRoute();

          this.isAppOnline = isAppOnline;

          if (!this.isAppOnline) {
            this.selectClientDialog?.close();
          }

          // this is needed in order to update the view
          this.changeDetectorRef.markForCheck();
        })
      )
      .subscribe();
  }

  toggleSidebar(): void {
    this.appFacade.toggleSidebar();
  }

  toggleFullScreenMode(): void {
    this.appFacade.toggleFullScreenMode();
  }

  toggleSupport(): void {
    this.zendeskService.toggle();
  }

  selectClient(): void {
    if (!this.isAppOnline) {
      return;
    }

    this.userDetailsFacade.query.userDetails$
      .pipe(
        take(1),
        switchMap((userDetails) => {
          this.selectClientDialog = this.dialog.open(
            SelectClientDialogComponent,
            {
              ...Constants.DialogOptions,
              panelClass: 'dialogCustom--mobileFullScreen',
              data: {
                clients: userDetails.clientsList,
                selectedClientId: userDetails.selectedClientId,
              },
            }
          );

          return this.selectClientDialog.afterClosed();
        }),
        filter((selectedClientId) => !!selectedClientId),
        switchMap((selectedClientId: string) =>
          this.selectedAccountService.selectClient(selectedClientId)
        ),
        tap(() => {
          this.toggleSidebar();
        })
      )
      .subscribe();
  }

  navigateToItem(menuItem: NavigationMenuItem): void {
    const baseURLFragments = ['/', this.clientId, menuItem.link];
    const routerParams = menuItem.params ?? menuItem.childLink;

    const route = routerParams
      ? [...baseURLFragments, routerParams]
      : baseURLFragments;

    this.router.navigate(route);

    if (this.isHandset) {
      this.toggleSidebar();
    }
  }

  updateUserPreferences(userPreferences: UserPreferences): void {
    this.genericHttpClientService
      .post<UserPreferences>({
        context: HttpSharedContext.SyncUserPreferences,
        body: userPreferences,
      })
      .pipe(
        untilDestroyed(this),
        tap((userPreferences) => {
          this.userDetailsFacade.setUserPreferences(userPreferences);
        })
      )
      .subscribe();
  }

  openUserPreferencesDialog(): void {
    this.isAppOnline$.pipe(take(1)).subscribe((isAppOnline) => {
      this.dialogRef = this.dialog.open(UserPreferencesDialogComponent, {
        ...Constants.DialogOptions,
        panelClass: 'dialogCustom--mobileFullScreen',
        data: {
          userPreferences: this.userDetails?.userPreferences,
          isAppOnline: isAppOnline,
          isHandset: this.isHandset,
        },
      });

      this.dialogRef.componentInstance.updateUserPreferences
        .pipe(untilDestroyed(this))
        .subscribe((userPreferences) =>
          this.updateUserPreferences(userPreferences)
        );
    });
  }

  signOut(): void {
    if (this.isAppOnline) {
      this.signOutService.signOut();
    }
  }

  //#region Private methods

  private setMenuItems(isAppOnline: boolean): void {
    this.menuItems$.next([
      {
        label: $localize`:@@shared.sidebar.lblDPRs:DPRs`,
        icon: 'calendar-month',
        link: 'dprs',
        additionalActivationLinks: ['dpr-details'],
        params: this.subprojectId,
        hasAccess: this.visibleMenuItems.includes(MenuItemType.Dprs),
        dataTestId: 'dprs',
      },
      {
        label: $localize`:@@shared.sidebar.lblProjectSetup:Project Setup`,
        icon: 'cog',
        link: 'project-setup',
        params: this.subprojectId,
        hasAccess:
          isAppOnline &&
          this.visibleMenuItems.includes(MenuItemType.ProjectSetup),
        dataTestId: 'project-setup',
      },
      {
        label: $localize`:@@shared.sidebar.lblClients:Clients`,
        icon: 'briefcase-variant',
        link: 'clients',
        hasAccess:
          isAppOnline && this.visibleMenuItems.includes(MenuItemType.Clients),
        dataTestId: 'clients',
      },
      {
        label: $localize`:@@shared.sidebar.lblSystemAdmin:System Admins`,
        icon: 'shield-account',
        link: 'user-admin',
        childLink: 'system-admins',
        hasAccess:
          isAppOnline &&
          this.visibleMenuItems.includes(MenuItemType.SystemAdmins),
        dataTestId: 'system-admin',
      },
      {
        label: $localize`:@@shared.sidebar.lblProjectAdmin:Project Admin`,
        icon: 'folder-open',
        link: 'project-admin',
        hasAccess:
          isAppOnline &&
          this.visibleMenuItems.includes(MenuItemType.ProjectAdmin),
        dataTestId: 'project-admin',
      },
      {
        label: $localize`:@@shared.sidebar.lblUsers:Users`,
        icon: 'account-group',
        link: 'user-admin',
        childLink: 'users',
        hasAccess:
          isAppOnline && this.visibleMenuItems.includes(MenuItemType.Users),
        dataTestId: 'user-admin',
      },
    ]);
  }

  private buildVisibleMenuItems(userDetails: UserSession): void {
    this.visibleMenuItems = [];
    if (userDetails.accountType === AccountType.SystemAdmin) {
      this.visibleMenuItems.push(
        MenuItemType.Clients,
        MenuItemType.SystemAdmins
      );
    }

    if (
      userDetails.accountType === AccountType.SystemAdmin ||
      userDetails.accountType === AccountType.ClientAdmin ||
      userDetails.userAdminRight !== Rights.None
    ) {
      this.visibleMenuItems.push(MenuItemType.ProjectAdmin);

      if (userDetails.clientsList?.length > 0) {
        this.visibleMenuItems.push(MenuItemType.Users);
      }
    }

    if (userDetails.selectedSubproject) {
      if (userDetails.selectedSubproject.projectSetup !== Rights.None) {
        this.visibleMenuItems.push(MenuItemType.ProjectSetup);
      }

      if (userDetails.selectedSubproject.dprScreen !== Rights.None) {
        this.visibleMenuItems.push(MenuItemType.Dprs);
      }
    }
  }

  private checkActivatedRoute(): void {
    const currentUrl = this.router.routerState.snapshot.url;

    this.menuItems$.value.forEach((item) => {
      const additionalActivationLinkInUrl =
        !!item.additionalActivationLinks?.some((x) => currentUrl.includes(x));

      const currentLinkInUrl =
        currentUrl.includes(item.link) &&
        (!item.childLink || currentUrl.includes(item.childLink));

      item.isLinkActive = additionalActivationLinkInUrl || currentLinkInUrl;
    });

    this.menuItems$.next(this.menuItems$.value);
  }

  //#endregion
}
